/* eslint-disable no-console */
import {
  ApolloLink,
  useApolloClient,
  useLazyQuery,
  useMutation,
} from '@apollo/client';
import { usePrevious } from '@uidotdev/usehooks';
import { first } from 'lodash';
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import { AppAlertMessage, AppContext } from './data/context/app-context';
import { useStoredConversationMessageItem } from './data/data/conversations/use-stored-conversation-message-item';
import { useDefaultHealthProviderItems } from './data/data/health-providers/use-default-health-provider-items';
import { createAuthErrorLink } from './data/GraphQL/api/auth-error-link';
import {
  CheckPinCodeActiveDocument,
  PinCodeLogoutDocument,
  PinCodeUpdateActiveDocument,
  PushType,
} from './data/types/api/graphql';
import { AuthStatus, refreshToken } from './firebase/authentication';
import { useAuth } from './hooks/use-auth';
import { useIdleTimer } from './hooks/use-idle-timer';
import {
  BaseLoginExpiredSignalRMessage,
  useSignalR,
} from './hooks/use-signalr';

const timeoutTime = 1000 * 60 * 20;
const UUID_PATTERN =
  /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i;

const DEFAULT_PATH = 'conversations';
const PINCODE_PATH = 'pincode';

type AppLoaderProps = PropsWithChildren;

export function AppLoader({ ...props }: AppLoaderProps) {
  const [alertMessages, setAlertMessage] = useState<AppAlertMessage[]>([]);

  const client = useApolloClient();
  const errorHandler = useRef<ApolloLink | null>(null);

  const { clean: cleanStoredMessages } = useStoredConversationMessageItem();

  const showAlertMessage = useCallback((value: AppAlertMessage) => {
    setAlertMessage(prevState => {
      const newState = [...prevState];

      newState.push({
        ...{
          id: uuid(),
        },
        ...value,
      });

      return newState;
    });
  }, []);

  const hideAlertMessage = useCallback((value: string) => {
    setAlertMessage(prevState =>
      [...prevState].filter(option => option.id !== value)
    );
  }, []);

  const {
    token,
    authUser,
    signIn,
    signOut,
    verifySMSCode,
    checkPinCode,
    checkOOBCode,
    setPinCode,
    forgotPassword,
    forgotPinCode,
    recoverPassword,
    verifyWithPassword,
    setPassword,
    setIdle,
    loginStatus: status,
    loading,
    me,
    reloadMe,
    validating,
    initialized,
  } = useAuth();

  const {
    connection,
    openConnection: openSignalRConnection,
    closeConnection: closeSignalRConnection,
    message: signalRMessage,
    addFilter,
    removeFilter,
    clearFilters,
  } = useSignalR();

  const [pinCodeUpdateActive] = useMutation(PinCodeUpdateActiveDocument, {
    fetchPolicy: 'network-only',
  });

  const [checkPinCodeActive] = useLazyQuery(CheckPinCodeActiveDocument, {
    fetchPolicy: 'network-only',
  });

  const [pinCodeLogout] = useMutation(PinCodeLogoutDocument, {
    fetchPolicy: 'network-only',
  });

  const onUpdate = useCallback(async () => {
    const result = await pinCodeUpdateActive();
    const isActive = result.data?.pinCodeUpdateActive.boolean;
    return isActive === true;
  }, [pinCodeUpdateActive]);

  const onCheck = useCallback(async () => {
    const result = await checkPinCodeActive();
    const isActive = result.data?.pinCodeActive === true;
    return isActive;
  }, [checkPinCodeActive]);

  const onLock = useCallback(async () => {
    cleanStoredMessages();
    await closeSignalRConnection();
    setIdle(window.location.pathname);
    await pinCodeLogout({ variables: { all: false } });
  }, [cleanStoredMessages, closeSignalRConnection, pinCodeLogout, setIdle]);

  // lock fires idle function and stops timer
  const { start, lock, stop } = useIdleTimer({
    timeout: timeoutTime,
    onIdle: onLock,
    heartbeat: {
      timeout: 30000,
      onUpdate,
      onCheck,
    },
  });

  const lockAll = useCallback(async () => {
    await pinCodeLogout({ variables: { all: true } });
  }, [pinCodeLogout]);

  useEffect(() => {
    if (connection && signalRMessage?.type === PushType.LoginExpired) {
      const message = signalRMessage as BaseLoginExpiredSignalRMessage;
      if (message.healthProfessionalId === me?.id) {
        const pinCodeId = localStorage.getItem('pincodeid');
        if (!message.pinCodeId && message.pinCodeId === pinCodeId) {
          console.log('lock signal received');
          lock();
        }
      }
    }
  }, [me?.id, lock, signalRMessage, connection]);

  // global error handler
  useEffect(() => {
    if (!errorHandler.current) {
      // Generic, operation-agnostic recovery for every query/mutation:
      // 428 -> pincode lock, 401 -> refresh-and-retry then re-login.
      errorHandler.current = createAuthErrorLink({
        onPinCodeLock: lock,
        refreshToken,
        onSignOut: signOut,
      });
      client.setLink(ApolloLink.from([errorHandler.current, client.link]));
    }
  }, [client, lock, signOut]);

  const location = useLocation();

  const navigate = useNavigate();

  // set healthprovider id
  const [healthProviderId, setHealthProviderId] = useState<string | null>(null);

  const { data: healthProviderItemsData } = useDefaultHealthProviderItems(
    !me || status?.status !== AuthStatus.AUTHORIZED
  );

  const category = useMemo(() => {
    const segements = location.pathname.replace(/^\/|\/$/g, '').split('/');
    if (segements.length > 1) {
      return segements[1];
    }
    return null;
  }, [location.pathname]);

  // clear filters on page change
  const previousCategory = usePrevious(category);
  useEffect(() => {
    if (category && previousCategory && previousCategory !== category) {
      clearFilters();
    }
  }, [category, clearFilters, healthProviderId, previousCategory]);

  // keep track of healthprovider

  useEffect(() => {
    if (status?.status === AuthStatus.AUTHORIZED) {
      // case 1: from pincode
      if (
        location?.state?.from &&
        location?.state?.from === PINCODE_PATH &&
        status?.path &&
        !status.path.includes(PINCODE_PATH)
      ) {
        console.log('NAVIGATE : case 1 from pincode', status?.path);
        navigate(status?.path ?? '/', { replace: true });
        return;
      }

      // get segments
      const segments = location.pathname.replace(/^\/|\/$/g, '').split('/');
      const firstSegment = segments[0];

      // case 2: no segments
      if ((!healthProviderId && !segments.length) || !firstSegment) {
        console.log(
          `NAVIGATE : case 2 first health provider and go to ${DEFAULT_PATH}`
        );
        const healthProvider = first(healthProviderItemsData);
        if (healthProvider) {
          navigate(`${healthProvider.id}/${DEFAULT_PATH}`, { replace: true });
          setHealthProviderId(healthProvider.id);
        }
        return;
      }

      // case 3: commimg from url
      const isValidUUID = UUID_PATTERN.test(firstSegment);
      const isKnownProvider = healthProviderItemsData?.some(
        provider => provider.id === firstSegment
      );

      // on switch health provider
      if (
        (isValidUUID || isKnownProvider) &&
        firstSegment !== healthProviderId
      ) {
        console.log(
          `NAVIGATE : case 3 different health provider then current ${firstSegment}`
        );
        setHealthProviderId(firstSegment);
        return;
      }

      // case 4: fallback if logged in but no health provider
      if (!isValidUUID) {
        const healthProvider = first(healthProviderItemsData);
        if (healthProvider) {
          navigate(`${healthProvider.id}/${DEFAULT_PATH}`, { replace: true });
          setHealthProviderId(healthProvider.id);
        }
      }
    }
  }, [
    status,
    healthProviderId,
    healthProviderItemsData,
    navigate,
    location.pathname,
    location?.state?.from,
  ]);

  /*
    useEffect(() => {
    if (status?.status === AuthStatus.AUTHORIZED) {
      if (!healthProviderId) {
        const healthProvider = first(healthProviderItemsData);

        if (healthProvider) {
          navigate(`${healthProvider.id}`, {
            replace: true,
          });
        }
      }
    }
  }, [status, healthProviderId, healthProviderItemsData, navigate]);
  */
  /*
  useEffect(() => {
    if (status?.status === AuthStatus.AUTHORIZED) {
      // Get path segments
      const segments = location.pathname.replace(/^\/|\/$/g, '').split('/');
      const firstSegment = segments[0];
      console.log('NAVIGATE : segments', segments, firstSegment);
      // Case 1: No segments (just root URL)
      if (!segments.length || !firstSegment) {
        console.log('NAVIGATE : case 1');
        const healthProvider = first(healthProviderItemsData);
        if (healthProvider) {
          navigate(`${healthProvider.id}/${DEFAULT_PATH}`, { replace: true });
          setHealthProviderId(healthProvider.id);
        }
        return;
      }

      // Case 2: Coming from pincode screen
      if (location?.state?.from && location?.state?.from === PINCODE_PATH) {
        console.log('NAVIGATE : case 2');
        navigate(status?.path ?? '/', { replace: true });
        return;
      }

      // Case 3: Check if the first segment is a valid healthProviderId
      const isValidUUID = UUID_PATTERN.test(firstSegment);
      const isKnownProvider = healthProviderItemsData?.some(
        provider => provider.id === firstSegment
      );

      // If valid provider ID in URL but different from current state
      if (
        (isValidUUID || isKnownProvider) &&
        firstSegment !== healthProviderId
      ) {
        console.log(`Updating provider ID from URL: ${firstSegment}`);
        setHealthProviderId(firstSegment);
        return;
      }

      // Case 4: Invalid first segment, redirect to current provider
      if (!isValidUUID && !isKnownProvider) {
        const healthProvider =
          healthProviderItemsData?.find(p => p.id === healthProviderId) ??
          first(healthProviderItemsData);

        if (healthProvider) {
          const page = segments[1] || DEFAULT_PATH;
          console.log('NAVIGATE : case 4', page, healthProvider.id);
          navigate(
            `${healthProvider.id}/${segments[1] || DEFAULT_PATH}${
              segments.length > 3 ? `/${segments.slice(3).join('/')}` : ''
            }`,
            { replace: true }
          );

          if (healthProviderId !== healthProvider.id) {
            setHealthProviderId(healthProvider.id);
          }
        }
      }
    }
  }, [
    status,
    healthProviderId,
    healthProviderItemsData,
    navigate,
    location?.state?.from,
    location.pathname,
  ]); */

  // on healthprovider change
  const onHandleUpdateHealthProviderId = useCallback(
    (newProviderId: string) => {
      if (newProviderId !== healthProviderId) {
        // Get path segments, skipping empty segments
        const path = location.pathname.startsWith('/')
          ? location.pathname.substring(1)
          : location.pathname;

        const segments = path.split('/').filter(Boolean);

        if (segments.length <= 1) {
          navigate(`/${newProviderId}/${DEFAULT_PATH}`);
          return;
        }

        // Replace first segment with new provider ID and filter the rest
        const newPath = [
          newProviderId,
          segments[1],
          ...segments.slice(2).filter(segment => !UUID_PATTERN.test(segment)),
        ].join('/');

        navigate(`/${newPath}`, {
          replace: true,
        });

        setHealthProviderId(prevState =>
          prevState !== newProviderId ? newProviderId : prevState
        );
      }
    },
    [healthProviderId, location.pathname, navigate]
  );

  // idle timer
  useEffect(() => {
    if (status?.status === AuthStatus.AUTHORIZED) {
      start();
    } else {
      stop();
    }
  }, [stop, start, status]);

  // start signalR
  useEffect(() => {
    (async () => {
      if (status) {
        if (status.status === AuthStatus.AUTHORIZED && healthProviderId) {
          await openSignalRConnection(import.meta.env.VITE_SIGNALR_ENDPOINT);
        } else {
          await closeSignalRConnection();
        }
      }
    })();
  }, [status, healthProviderId, openSignalRConnection, closeSignalRConnection]);

  const value = useMemo(
    () => ({
      status,
      token,
      loading,
      authUser,
      validating,
      initialized,
      lock,
      lockAll,
      // setIdle,
      signIn,
      signOut,
      verifySMSCode,
      checkPinCode,
      checkOOBCode,
      forgotPinCode,
      setPinCode,
      verifyWithPassword,
      setPassword,
      forgotPassword,
      recoverPassword,
      timeout: timeoutTime,
      user: me,
      reloadUser: reloadMe,
      healthProviderId,
      healthProviders: healthProviderItemsData,
      updateHealthProviderId: onHandleUpdateHealthProviderId,
      alertMessages,
      signalRMessage,
      addFilter,
      removeFilter,
      clearFilters,
      connection,
      showAlertMessage,
      hideAlertMessage,
    }),
    [
      status,
      token,
      loading,
      authUser,
      validating,
      initialized,
      lock,
      lockAll,
      // setIdle,
      signIn,
      signOut,
      verifySMSCode,
      checkPinCode,
      checkOOBCode,
      forgotPinCode,
      setPinCode,
      verifyWithPassword,
      setPassword,
      forgotPassword,
      recoverPassword,
      me,
      reloadMe,
      healthProviderId,
      healthProviderItemsData,
      onHandleUpdateHealthProviderId,
      alertMessages,
      signalRMessage,
      addFilter,
      clearFilters,
      removeFilter,
      connection,
      showAlertMessage,
      hideAlertMessage,
    ]
  );

  return (
    <AppContext.Provider value={value}>{props.children}</AppContext.Provider>
  );
}
